import {
  computed,
  Injectable,
  signal,
} from '@angular/core';

import type {
  AccountRole,
  TokensResponse,
} from '../../../features/auth/models/auth.models';

const ACCESS_TOKEN_KEY =
  'werkraum_access_token';
const REFRESH_TOKEN_KEY =
  'werkraum_refresh_token';
const ACCOUNT_ROLE_KEY =
  'werkraum_account_role';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly accessTokenState =
    signal<string | null>(
      this.readLocal(ACCESS_TOKEN_KEY),
    );

  private readonly refreshTokenState =
    signal<string | null>(
      this.readLocal(REFRESH_TOKEN_KEY),
    );

  private readonly accountRoleState =
    signal<AccountRole | null>(
      this.readAccountRole(),
    );

  readonly accessToken =
    this.accessTokenState.asReadonly();

  readonly refreshToken =
    this.refreshTokenState.asReadonly();

  readonly accountRole =
    this.accountRoleState.asReadonly();

  readonly isAuthenticated = computed(
    () =>
      Boolean(
        this.accessTokenState() ||
          this.refreshTokenState(),
      ),
  );

  setTokens(
    tokens: TokensResponse,
    role?: AccountRole,
  ): void {
    this.accessTokenState.set(
      tokens.access_token,
    );
    this.refreshTokenState.set(
      tokens.refresh_token,
    );

    this.writeLocal(
      ACCESS_TOKEN_KEY,
      tokens.access_token,
    );
    this.writeLocal(
      REFRESH_TOKEN_KEY,
      tokens.refresh_token,
    );

    if (role) {
      this.setAccountRole(role);
    }
  }

  setAccountRole(
    role: AccountRole,
  ): void {
    this.accountRoleState.set(role);
    this.writeLocal(
      ACCOUNT_ROLE_KEY,
      role,
    );
  }

  getAccessToken(): string | null {
    return this.accessTokenState();
  }

  getRefreshToken(): string | null {
    return this.refreshTokenState();
  }

  getAccountRole(): AccountRole | null {
    return this.accountRoleState();
  }

  hasTokens(): boolean {
    return Boolean(
      this.getAccessToken() ||
        this.getRefreshToken(),
    );
  }

  hasRole(
    role: AccountRole,
  ): boolean {
    return (
      this.accountRoleState() === role
    );
  }

  getRoleHomeUrl(
    role:
      | AccountRole
      | null = this.getAccountRole(),
  ): string | null {
    switch (role) {
      case 'admin':
        return '/staff/admin/projects';

      case 'staff':
        return '/staff/employee/dashboard';

      case 'partner':
        return '/partner/projects';

      case 'client':
      default:
        return null;
    }
  }

  clearTokens(): void {
    this.accessTokenState.set(null);
    this.refreshTokenState.set(null);
    this.accountRoleState.set(null);

    this.removeLocal(ACCESS_TOKEN_KEY);
    this.removeLocal(REFRESH_TOKEN_KEY);
    this.removeLocal(ACCOUNT_ROLE_KEY);
  }

  private readAccountRole(): AccountRole | null {
    const value = this.readLocal(
      ACCOUNT_ROLE_KEY,
    );

    return this.isAccountRole(value)
      ? value
      : null;
  }

  private isAccountRole(
    value: string | null,
  ): value is AccountRole {
    return (
      value === 'admin' ||
      value === 'staff' ||
      value === 'client' ||
      value === 'partner'
    );
  }

  private readLocal(
    key: string,
  ): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeLocal(
    key: string,
    value: string,
  ): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(
        key,
        value,
      );
    } catch {
      // Storage может быть недоступен.
    }
  }

  private removeLocal(
    key: string,
  ): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.removeItem(key);
    } catch {
      // Storage может быть недоступен.
    }
  }
}
